// src/pages/administracion/facturacion-electronica/FacturacionElectronica.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid, IconButton, LinearProgress, MenuItem, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Tooltip, Typography, Paper,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  ReceiptLong as ReceiptIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import PageLayout from '../../../components/layout/PageLayout';
import { toast } from '../../../utils/toast';
import {
  FacturacionElectronicaApi, ResolucionDian, ResolucionPayload,
} from '../../../services/api/facturacionElectronicaApi';
import PanelProveedores from './PanelProveedores';

const VACIO: ResolucionPayload = {
  NumeroResolucion: '', Prefijo: '', RangoDesde: 1, RangoHasta: 1000,
  FechaDesde: '', FechaHasta: '', ClaveTecnica: '',
  Ambiente: 'SANDBOX', Activo: true,
};

const soloFecha = (v?: string) => (v ? String(v).slice(0, 10) : '');

const FacturacionElectronica: React.FC = () => {
  const [resoluciones, setResoluciones] = useState<ResolucionDian[]>([]);
  const [cargando, setCargando] = useState(true);
  const [abierto, setAbierto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [form, setForm] = useState<ResolucionPayload>(VACIO);

  const cargar = useCallback(async () => {
    try {
      setCargando(true);
      setResoluciones(await FacturacionElectronicaApi.listarResoluciones());
    } catch {
      toast.error('No se pudieron cargar las resoluciones');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirNueva = () => { setEditandoId(null); setForm(VACIO); setAbierto(true); };

  const abrirEdicion = (r: ResolucionDian) => {
    setEditandoId(r.Id);
    setForm({
      NumeroResolucion: r.NumeroResolucion, Prefijo: r.Prefijo,
      RangoDesde: r.RangoDesde, RangoHasta: r.RangoHasta,
      NumeroActual: r.NumeroActual,
      FechaDesde: soloFecha(r.FechaDesde), FechaHasta: soloFecha(r.FechaHasta),
      ClaveTecnica: r.ClaveTecnica ?? '', Ambiente: r.Ambiente, Activo: r.Activo,
    });
    setAbierto(true);
  };

  const campo = (k: keyof ResolucionPayload) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const guardar = async () => {
    if (!form.NumeroResolucion.trim() || !form.Prefijo.trim()) {
      toast.warning('El número de resolución y el prefijo son obligatorios');
      return;
    }
    if (!form.FechaDesde || !form.FechaHasta) {
      toast.warning('Indica la vigencia (fecha desde y hasta)');
      return;
    }
    if (Number(form.RangoHasta) < Number(form.RangoDesde)) {
      toast.warning('El rango final debe ser mayor o igual al inicial');
      return;
    }
    try {
      setGuardando(true);
      const dto: ResolucionPayload = {
        ...form,
        RangoDesde: Number(form.RangoDesde),
        RangoHasta: Number(form.RangoHasta),
        NumeroActual: form.NumeroActual !== undefined ? Number(form.NumeroActual) : undefined,
      };
      if (editandoId) await FacturacionElectronicaApi.actualizarResolucion(editandoId, dto);
      else await FacturacionElectronicaApi.crearResolucion(dto);
      toast.success(editandoId ? 'Resolución actualizada' : 'Resolución creada');
      setAbierto(false);
      cargar();
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'No se pudo guardar la resolución');
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (r: ResolucionDian) => {
    const res = await Swal.fire({
      title: '¿Eliminar resolución?',
      text: `Se eliminará ${r.Prefijo} ${r.NumeroResolucion}. No afecta a las facturas ya emitidas.`,
      icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Sí, eliminar', cancelButtonText: 'Cancelar', reverseButtons: true,
    });
    if (!res.isConfirmed) return;
    try {
      await FacturacionElectronicaApi.eliminarResolucion(r.Id);
      toast.success('Resolución eliminada');
      cargar();
    } catch {
      toast.error('No se pudo eliminar');
    }
  };

  // Consumo del rango: avisa antes de que se agote la numeración
  const consumo = useMemo(() => (r: ResolucionDian) => {
    const total = Number(r.RangoHasta) - Number(r.RangoDesde) + 1;
    const usados = Math.max(0, Number(r.NumeroActual) - Number(r.RangoDesde) + 1);
    return { usados, total, pct: total > 0 ? Math.min(100, (usados / total) * 100) : 0 };
  }, []);

  return (
    <PageLayout icon={ReceiptIcon} title="Facturación Electrónica">
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={800}>Resoluciones DIAN</Typography>
            <Typography variant="body2" color="text.secondary">
              Numeración autorizada para emitir facturas electrónicas.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNueva}>
            Nueva resolución
          </Button>
        </Stack>

        {cargando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : resoluciones.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 5, textAlign: 'center' }}>
            <ReceiptIcon sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" fontWeight={600}>Sin resoluciones configuradas</Typography>
            <Typography variant="body2" color="text.secondary">
              Registra la resolución que te autorizó la DIAN para poder facturar.
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Resolución</TableCell>
                  <TableCell>Prefijo</TableCell>
                  <TableCell>Rango</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>Consumo</TableCell>
                  <TableCell>Vigencia</TableCell>
                  <TableCell>Ambiente</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resoluciones.map((r) => {
                  const c = consumo(r);
                  return (
                    <TableRow key={r.Id} hover>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{r.NumeroResolucion}</TableCell>
                      <TableCell><strong>{r.Prefijo}</strong></TableCell>
                      <TableCell>{r.RangoDesde} – {r.RangoHasta}</TableCell>
                      <TableCell>
                        <LinearProgress
                          variant="determinate" value={c.pct}
                          color={c.pct > 90 ? 'error' : c.pct > 70 ? 'warning' : 'primary'}
                          sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {c.usados} de {c.total}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {soloFecha(r.FechaDesde)} → {soloFecha(r.FechaHasta)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={r.Ambiente}
                          color={r.Ambiente === 'PRODUCCION' ? 'success' : 'default'}
                          variant={r.Ambiente === 'PRODUCCION' ? 'filled' : 'outlined'} />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={r.Activo ? 'Activa' : 'Inactiva'}
                          color={r.Activo ? 'primary' : 'default'} />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => abrirEdicion(r)}><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => eliminar(r)}><DeleteIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <PanelProveedores />
      </Box>

      {/* Formulario */}
      <Dialog open={abierto} onClose={() => setAbierto(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {editandoId ? 'Editar resolución' : 'Nueva resolución DIAN'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth label="N° de resolución" value={form.NumeroResolucion}
                onChange={campo('NumeroResolucion')} required />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Prefijo" value={form.Prefijo}
                onChange={campo('Prefijo')} required />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="number" label="Numeración desde" value={form.RangoDesde}
                onChange={campo('RangoDesde')} required />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="number" label="Numeración hasta" value={form.RangoHasta}
                onChange={campo('RangoHasta')} required />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="date" label="Vigente desde" InputLabelProps={{ shrink: true }}
                value={form.FechaDesde} onChange={campo('FechaDesde')} required />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="date" label="Vigente hasta" InputLabelProps={{ shrink: true }}
                value={form.FechaHasta} onChange={campo('FechaHasta')} required />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Ambiente" value={form.Ambiente}
                onChange={campo('Ambiente')}
                helperText="Producción emite facturas con validez fiscal">
                <MenuItem value="SANDBOX">Sandbox (pruebas)</MenuItem>
                <MenuItem value="PRODUCCION">Producción</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Estado" value={form.Activo ? '1' : '0'}
                onChange={(e) => setForm((f) => ({ ...f, Activo: e.target.value === '1' }))}
                helperText="Solo una activa por ambiente">
                <MenuItem value="1">Activa</MenuItem>
                <MenuItem value="0">Inactiva</MenuItem>
              </TextField>
            </Grid>
            {editandoId && (
              <Grid item xs={12}>
                <TextField fullWidth type="number" label="Consecutivo actual"
                  value={form.NumeroActual ?? 0} onChange={campo('NumeroActual')}
                  helperText="Último número usado. Modifícalo solo si sabes lo que haces: un valor incorrecto genera numeración duplicada y la DIAN rechaza el documento." />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField fullWidth label="Clave técnica (opcional)" value={form.ClaveTecnica ?? ''}
                onChange={campo('ClaveTecnica')} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setAbierto(false)} disabled={guardando}>Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={guardando}
            startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : undefined}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
};

export default FacturacionElectronica;
